/**
 * Keeps the current real weather for wherever the player is.
 *
 * Fetches Open-Meteo's current conditions on start, again every fifteen
 * minutes — its own update interval — and whenever the player has travelled
 * far enough (25 km) for the weather to be somewhere else. A failed request
 * keeps the last good weather; there is nothing worse than a sky that blinks
 * to clear because a request timed out.
 */

import type { Weather } from '../core/weather/weather.js';
import { CLEAR, fromOpenMeteo, openMeteoUrl } from '../core/weather/weather.js';

const REFRESH_MS = 15 * 60 * 1000;
const MOVE_KM = 25;

export class WeatherService {
  current: Weather = CLEAR;
  /** True once real data has arrived. */
  live = false;
  private lastFetch = -Infinity;
  private lastLat = NaN;
  private lastLon = NaN;
  private inFlight = false;

  constructor(private readonly fixed?: Weather) {
    if (fixed !== undefined) this.current = fixed;
  }

  update(lat: number, lon: number, now = performance.now()): void {
    if (this.fixed !== undefined || this.inFlight) return;
    const moved = Number.isNaN(this.lastLat)
      ? Infinity
      : Math.hypot(lat - this.lastLat, (lon - this.lastLon) * Math.cos((lat * Math.PI) / 180)) * 111;
    if (now - this.lastFetch < REFRESH_MS && moved < MOVE_KM) return;
    this.inFlight = true;
    this.lastFetch = now;
    this.lastLat = lat;
    this.lastLon = lon;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    fetch(openMeteoUrl(lat, lon), { signal: controller.signal })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((json: unknown) => {
        this.current = fromOpenMeteo(json);
        this.live = true;
      })
      .catch(() => { /* keep the last good weather */ })
      .finally(() => {
        clearTimeout(timeout);
        this.inFlight = false;
      });
  }
}
